"use client";

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import styles from "./Table.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import Spinner from "./spinner";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from "./ui/select";

const PAGE_SIZE = 50;
const DATE_LIKE = /^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}/;

const Table = ({
  columns,
  data,
  height,
  isRowClickable,
  onRowClick,
  loading,
  getRowClassName,
  showCheckboxes = false,
  onSelectionChange,
  selectedIds = [],
  rowColor,
  isRowDisabled = () => false,
  defaultSortKey = null,
  defaultSortDirection = "asc",
  onSort,
  frozenColumns = [],
  // New props for pagination
  pagination = false,
  meta = null,
  onPageChange,
  paginationLoading = false,
  // "default" keeps today's chevron-only footer. "expanded" opts a
  // consumer into the redesigned Back/Next + windowed-numbers + range
  // summary + page-size-dropdown footer (currently Billing Dashboard only).
  paginationVariant = "default",
  // Page-size dropdown (expanded variant only): only renders when both
  // are passed, so existing pagination={true} consumers see no change.
  pageSize,
  onPageSizeChange,
  // New props for infinite scroll
  infiniteScroll = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  totalItems: infiniteTotalItems = 0,
  // Primary header variant
  primaryHeader = false,
  // Optional per-column min-width overrides ({ [columnKey]: px }), checked
  // before the hardcoded defaults in getColumnWidth. Opt-in only - consumers
  // that don't pass this see no change.
  columnWidths = {},
  // Opt-in: collapse the table's internal scroll container so the page
  // owns the scroll. The sticky <thead> sticks to the viewport (offset
  // by --toca-sticky-top-offset on an ancestor) instead of an inner box.
  disableInternalScroll = false,
  // Opt-in: splits the header out of the vertically-scrolling box entirely
  // (separate <table> for header vs. body) so the vertical scrollbar only
  // ever belongs to the body content, never the header. Horizontal scroll
  // still moves header and body together (shared outer scroll container).
  // Off by default - existing consumers render exactly as before.
  splitHeaderScroll = false,
  // Opt-in: the whole table (header + scrollable body + pagination footer)
  // becomes a sticky region filling the viewport from just below the fixed
  // nav down to the bottom of the screen - header pinned at its top,
  // pagination footer pinned at its bottom, only the rows in between
  // scroll. Intended for use together with splitHeaderScroll. Off by
  // default - existing consumers render exactly as before.
  stickyTableRegion = false,
  // Opt-in: visually hides the body's own native horizontal scrollbar
  // (still fully scrollable - wheel/trackpad/programmatic scrollLeft all
  // still work) for consumers that render their own scroll indicator
  // elsewhere instead. Off by default - existing consumers render exactly
  // as before.
  hideHorizontalScrollbar = false,
  // Text of the single row shown when `data` is empty.
  emptyMessage = "No records found"
}) => {
  const [sortConfig, setSortConfigState] = useState({
    key: defaultSortKey,
    direction: defaultSortKey ? defaultSortDirection : "none",
    extra: undefined,
  });

  const setSortConfig = useCallback((config) => {
    setSortConfigState(config);
  }, []);

  // Ref to prevent duplicate scroll triggers
  const isLoadingRef = useRef(false);
  const [selectedRows, setSelectedRows] = useState(new Set(selectedIds));
  const containerRef = useRef(null);
  // splitHeaderScroll only: the header's own (hidden-scrollbar) horizontal
  // scroll container, kept in sync with the body's real horizontal scroll
  // via handleBodyHorizontalScroll below - see the comment there for why a
  // shared outer scroll container can't be used instead.
  const headerScrollRef = useRef(null);
  const handleBodyHorizontalScroll = useCallback(e => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  }, []);
  const [allSelected, setAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Memoize sorted data to prevent recalculation on every render
  const sortedData = useMemo(() => {
    const tableData = Array.isArray(data) ? data : [];

    return [...tableData].sort((a, b) => {
      if (!sortConfig.key || sortConfig.direction === "none") return 0;
      const column = columns.find(col => col.key === sortConfig.key);
      if (!column) return 0;

      if (typeof column.customSort === "function") {
        return column.customSort(a, b, sortConfig.direction, sortConfig.extra || {}, column);
      }

      // Use sortField if provided, otherwise use key
      const sortKey = column.sortField || sortConfig.key;

      let aValue = a[sortKey];
      let bValue = b[sortKey];

      // Handle null/undefined values first - push all nulls to the end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Try to convert to numbers if possible
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      if (!isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum)) {
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Try to detect and compare dates
      if (
        typeof aValue === "string" &&
        typeof bValue === "string" &&
        DATE_LIKE.test(aValue) &&
        DATE_LIKE.test(bValue)
      ) {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }
      }

      // Default to string comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [data, columns, sortConfig]);

  // Determine Visible Rows (Server vs Client Logic)
  // `showPagination` below only renders the page-nav UI when the caller
  // opted in via `pagination` (server-paginated, e.g. Billing Dashboard).
  // Without that prop there's no way for the user to reach a page 2, so
  // slicing to PAGE_SIZE here would silently strand every row past the
  // first 50 with no indication anything is missing - render the caller's
  // full list instead (e.g. the dashboard's silent background pagination,
  // which hands us its whole accumulated list once loaded).
  const visibleRows = useMemo(() => {
    if (!pagination) return sortedData;
    return sortedData; // Server already sliced to one page
  }, [sortedData, pagination, currentPage]);

  // Calculate Pagination Stats
  const displayPage = pagination ? (meta?.current_page || 1) : currentPage;
  const totalPages = pagination ? (meta?.total_pages || 1) : Math.ceil(sortedData.length / PAGE_SIZE);
  const totalItems = pagination ? (meta?.total_items || 0) : sortedData.length;
  
  // Handlers
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    if (pagination && onPageChange) {
      onPageChange(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    if(!pagination) setCurrentPage(1);
  }, [data, sortConfig, pagination]);

  useEffect(() => {
    setSelectedRows(prevSelected => {
      const prevSelectedIds = Array.from(prevSelected);
      if (JSON.stringify(prevSelectedIds) !== JSON.stringify(selectedIds)) {
        return new Set(selectedIds);
      }
      return prevSelected; 
    });
  }, [selectedIds]);

  useEffect(() => {
    if (sortedData.length > 0 && selectedRows.size === sortedData.length) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [selectedRows, sortedData]);

  useEffect(() => {
    if (onSort) {
      onSort(sortedData);
    }
  }, [sortedData, onSort]);

  // Sync ref with loadingMore prop to prevent race conditions
  useEffect(() => {
    isLoadingRef.current = loadingMore;
  }, [loadingMore]);

  // Function to trigger load more
  const triggerLoadMore = useCallback(() => {
    if (!infiniteScroll || !containerRef.current || isLoadingRef.current || loadingMore || !hasMore) return false;

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Trigger load more when user is within 100px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      if (onLoadMore) {
        // Set ref immediately to prevent duplicate calls
        isLoadingRef.current = true;
        onLoadMore();
        return true;
      }
    }
    return false;
  }, [infiniteScroll, hasMore, loadingMore, onLoadMore]);

  // Scroll event handler - triggers when scrolling within the container
  const handleScroll = useCallback(() => {
    triggerLoadMore();
  }, [triggerLoadMore]);

  // Wheel event handler - triggers when user tries to scroll down while at bottom
  // This is needed because scroll events don't fire when already at the bottom
  const handleWheel = useCallback((e) => {
    if (!infiniteScroll || !containerRef.current || isLoadingRef.current || loadingMore || !hasMore) return;

    // Only trigger on downward scroll (positive deltaY)
    if (e.deltaY > 0) {
      const container = containerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Check if we're at or very near the bottom
      if (scrollHeight - scrollTop - clientHeight < 10) {
        triggerLoadMore();
      }
    }
  }, [infiniteScroll, hasMore, loadingMore, triggerLoadMore]);

  // Attach scroll and wheel listeners for infinite scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !infiniteScroll) return;

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('wheel', handleWheel);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [infiniteScroll, handleScroll, handleWheel]);

  const handleSort = useCallback(key => {
    setSortConfigState(prev => {
      if (prev.key === key) {
        if (prev.direction === "asc") return { key, direction: "desc", extra: prev.extra };
        if (prev.direction === "desc") return { key: null, direction: "none", extra: undefined };
      }
      return { key, direction: "asc", extra: prev.key === key ? prev.extra : undefined };
    });
  }, []);

  const handleRowSelection = id => {
    setSelectedRows(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }

      if (onSelectionChange) {
        onSelectionChange(Array.from(newSelected));
      }

      return newSelected;
    });
  };

  const handleSelectAll = e => {
    if (e.target.checked) {
      const allIds = sortedData.map(row => row.id);
      setSelectedRows(new Set(allIds));
      setAllSelected(true);
      if (onSelectionChange) {
        onSelectionChange(allIds);
      }
    } else {
      setSelectedRows(new Set());
      setAllSelected(false);
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }
  };

  const truncateText = content => {
    if (typeof content !== "string") {
      return content;
    }
    const words = content.split(" ");
    if (words.length > 5) {
      return words.slice(0, 5).join(" ") + "...";
    }
    return content;
  };

  const checkboxColumn = showCheckboxes
    ? [
        {
          key: "checkbox",
          label: (
            <label
              className={styles.checkboxLabel}
              onClick={e => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className={styles.customCheckbox}
                checked={allSelected && sortedData.length > 0}
                onChange={handleSelectAll}
              />
            </label>
          ),
          sorting: false,
          render: row => (
            <label
              className={styles.checkboxLabel}
              onClick={e => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={selectedRows.has(row.id)}
                onChange={() => handleRowSelection(row.id)}
                className={styles.customCheckbox}
              />
            </label>
          )
        }
      ]
    : [];

  const allColumns = [...checkboxColumn, ...columns];

  const isColumnFrozen = columnKey => {
    return frozenColumns.includes(columnKey);
  };

  const getColumnWidth = columnKey => {
    if (columnWidths[columnKey]) return columnWidths[columnKey];
    if (columnKey === "checkbox") return 60;
    if (columnKey === "name" || columnKey === "patient_name") return 180;
    if (columnKey === "facility") return 120;
    if (columnKey === "dob" || columnKey.includes("Date")) return 180;
    return 100;
  };

  const getFrozenColumnLeft = columnKey => {
    const frozenIndex = frozenColumns.indexOf(columnKey);
    if (frozenIndex === -1) return 0;

    let leftOffset = 0;
    for (let i = 0; i < frozenIndex; i++) {
      const prevColumn = allColumns.find(col => col.key === frozenColumns[i]);
      if (prevColumn) {
        leftOffset += getColumnWidth(prevColumn.key);
      }
    }
    return leftOffset;
  };

  const showPagination =
    !infiniteScroll &&
    pagination &&
    totalPages > 1;

  // Expanded variant's summary + page-size dropdown should stay visible
  // even when everything fits on one page (e.g. fewer records than the
  // selected page size) - otherwise a user stuck on a too-large page size
  // has no visible control to switch back down. Only the Back/Next +
  // page-number nav (showPagination, below) hides itself in that case.
  const showExpandedFooter =
    !infiniteScroll &&
    pagination &&
    paginationVariant === "expanded" &&
    totalItems > 0;

  // Windowed page-number list: first, last, current ± 2, with ellipses
  // filling any gaps.
  const pageNumbers = useMemo(() => {
    if (!showPagination) return [];

    const windowStart = Math.max(1, displayPage - 2);
    const windowEnd = Math.min(totalPages, displayPage + 2);

    const pages = [];
    pages.push(1);
    if (windowStart > 2) pages.push("...");
    for (let p = Math.max(2, windowStart); p <= Math.min(totalPages - 1, windowEnd); p++) {
      pages.push(p);
    }
    if (windowEnd < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  }, [showPagination, displayPage, totalPages]);

  // Windowed page-number list for the "expanded" variant: first 3, last 3,
  // and current ± 1, merged/deduped into contiguous runs so an ellipsis
  // only appears where an actual gap remains between groups.
  const pageNumbersExpanded = useMemo(() => {
    if (!showPagination) return [];
    if (totalPages <= 9) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const first = [1, 2, 3];
    const last = [totalPages - 2, totalPages - 1, totalPages];
    const middle = [displayPage - 1, displayPage, displayPage + 1];

    const uniqueSorted = Array.from(new Set([...first, ...middle, ...last]))
      .filter(p => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);

    const pages = [];
    uniqueSorted.forEach((p, i) => {
      if (i > 0 && p - uniqueSorted[i - 1] > 1) pages.push("...");
      pages.push(p);
    });

    return pages;
  }, [showPagination, displayPage, totalPages]);

  // Column width: explicit `width` (not just minWidth) only in split mode,
  // so table-layout:fixed can't let either the header-only or body-only
  // <table> drift to a different computed width than the other - they're
  // now two independent tables that need to agree on widths by rule, not
  // by sharing a single <table>'s layout the way the default mode does.
  // columnWidths[colKey] = { auto: true, minWidth } is a deliberate escape
  // hatch from that: per the CSS2.1 fixed-table-layout algorithm, a column
  // with an explicit `width` is sized to exactly that, full stop - only a
  // column with no specified width receives whatever's left over once the
  // explicit ones are subtracted from the table's own width. Marking one
  // column this way (rather than every column having an explicit px width
  // and the table having none left to designate) is what lets the table
  // fill its container without table-layout:fixed spreading that leftover
  // space proportionally across every column instead. minWidth is a real
  // CSS min-width constraint layered on top (table-layout:fixed's
  // algorithm itself only reads `width`, not `min-width`, from the first
  // row), so the column still won't shrink below it.
  // Opt-in only (no existing caller passes this shape), so it's a no-op
  // for every column that still gets a plain number.
  const getCellWidthStyle = colKey => {
    const configured = columnWidths[colKey];
    if (configured && typeof configured === "object" && configured.auto) {
      return {
        width: "auto",
        minWidth: `${configured.minWidth ?? 0}px`
      };
    }
    return {
      minWidth: `${getColumnWidth(colKey)}px`,
      width: splitHeaderScroll
        ? `${getColumnWidth(colKey)}px`
        : colKey === "checkbox"
        ? "60px"
        : "auto"
    };
  };

  const theadRow = (
    <tr>
      {allColumns.map((col, index) => {
        const isFrozen = isColumnFrozen(col.key);
        return (
          <th
            key={col.key}
            className={`${
              col.sorting === false ? styles.unsorted : ""
            } ${isFrozen ? styles.frozen_column : ""}`}
            style={{
              textAlign: col.alignLeft ? "left" : "center",
              ...getCellWidthStyle(col.key),
              ...(isFrozen && {
                position: "sticky",
                left: `${getFrozenColumnLeft(col.key)}px`,
                zIndex: 11
              })
            }}
            onClick={
              col.customHeader || col.sorting === false
                ? null
                : () => handleSort(col.key)
            }
          >
            {col.customHeader ? (
              col.customHeader({
                column: col,
                sortConfig,
                setSortConfig,
                toggleSort: handleSort,
                isSorted:
                  sortConfig.key === col.key &&
                  sortConfig.direction !== "none",
                sortType: sortConfig.extra?.type,
                sortDirection: sortConfig.direction,
              })
            ) : (
              <>
                {typeof col.label === "function"
                  ? col.label()
                  : col.label}
                {col.key !== "checkbox" && col.sorting !== false ? (
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === col.key
                        ? sortConfig.direction === "asc"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={`${styles.sortIcon} ${
                      sortConfig.key === col.key ? styles.active : ""
                    }`}
                  />
                ) : null}
              </>
            )}
          </th>
        );
      })}
    </tr>
  );

  const tbodyContent = loading ? (
    <tbody className={styles.body_table}>
      <tr>
        <td colSpan={allColumns.length} style={{ textAlign: "center" }}>
          <Spinner />
        </td>
      </tr>
    </tbody>
  ) : (
    <tbody className={styles.body_table}>
      {visibleRows && visibleRows.length > 0 ? (
        visibleRows.map((row, index) => {
          const disabled = isRowDisabled(row);
          const isSelected = selectedRows.has(row.id);
          const resolvedRowBg = rowColor ? rowColor(row, index) : "transparent";
          const rowBackground = disabled ? "#f5f5f5" : resolvedRowBg;
          // Frozen (sticky) cells need an opaque background of their own -
          // otherwise columns scrolled underneath them would show through.
          // That was hardcoded to white, which also silently swallowed any
          // per-row tint from rowColor under the frozen column specifically.
          // Mirroring rowBackground here (falling back to white only when
          // there's nothing to mirror) keeps that opacity guarantee while
          // letting the frozen cell match the rest of the row.
          const frozenCellBackground =
            rowBackground === "transparent" ? "#ffffff" : rowBackground;

          return (
            <tr
              key={row.id || index}
              style={{
                cursor: isRowClickable ? "pointer" : "default",
                backgroundColor: rowBackground,
                "--frozen-cell-bg": frozenCellBackground,
                ...(disabled ? { opacity: 0.5 } : {})
              }}
              className={`${
                getRowClassName ? getRowClassName(row) : ""
              } ${isSelected ? styles.selected_row : ""} ${
                disabled ? styles.disabled_row : ""
              }`}
              onClick={() => !disabled && onRowClick && onRowClick(row)}
            >
              {allColumns.map(col => {
                const isFrozen = isColumnFrozen(col.key);
                return (
                  <td
                    key={col.key}
                    className={isFrozen ? styles.frozen_column : ""}
                    style={{
                      textAlign: col.alignLeft ? "left" : "center",
                      ...getCellWidthStyle(col.key),
                      padding: "12px 16px",
                      ...(isFrozen && {
                        position: "sticky",
                        left: `${getFrozenColumnLeft(col.key)}px`,
                        zIndex: 2
                      })
                    }}
                  >
                    {truncateText(
                      col.render ? col.render(row) : row[col.key]
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })
      ) : (
        <tr>
          <td
            colSpan={allColumns.length}
            style={{ textAlign: "center" }}
          >
            {emptyMessage}
          </td>
        </tr>
      )}
    </tbody>
  );

  // stickyTableRegion doesn't need the header's own position:sticky offset
  // logic - the OUTER sticky region (below) already sticks the whole table
  // below the nav, and inside it the header is just a non-scrolling flex
  // item pinned at the top by normal flex layout, not by its own stickiness.
  const headerClassName = `${styles.header_table} ${primaryHeader ? styles.primary_header : ""} ${disableInternalScroll && !stickyTableRegion ? styles.no_internal_scroll : ""}`;

  return (
    <div className={stickyTableRegion ? styles.sticky_table_region : undefined}>
      <div className={`${styles.table_wrapper} ${stickyTableRegion ? styles.sticky_table_wrapper : ""}`}>
        {splitHeaderScroll ? (
          // Header lives in its own <table>, outside the vertically
          // scrolling box - so the vertical scrollbar only ever belongs to
          // the body. Horizontal scroll wraps both together so they still
          // move in sync sideways.
          //
          // stickyTableRegion: body flex-fills the sticky region's fixed
          // height (see .split_body_scroll.sticky_table_wrapper rule),
          // instead of a page-scroll or bounded-px-height mode.
          //
          // disableInternalScroll (without stickyTableRegion) means the
          // *page* owns vertical scroll instead of a bounded inner box: the
          // body renders with no height cap, and the header (position:sticky,
          // offset by --toca-sticky-top-offset via .no_internal_scroll in
          // headerClassName) sticks to the viewport below the fixed nav as
          // the page scrolls.
          <div
            className={`${styles.split_scroll_outer} ${stickyTableRegion ? styles.sticky_table_wrapper : ""}`}
            // disableInternalScroll: both axes need to be genuinely
            // visible, not just overflow-y - per the CSS overflow spec, if
            // one axis is non-visible (this element's own class still has
            // overflow-x:auto) the browser silently forces the other axis
            // to auto too, even when set to visible inline. Any ancestor
            // that ends up non-visible on either axis becomes the nearest
            // "scroll container" for position:sticky purposes, hijacking
            // it away from the real page scroll it's supposed to resolve
            // against - which is exactly why the header/footer weren't
            // sticking. Both explicitly visible together avoids the
            // pairing rule entirely.
            style={
              disableInternalScroll && !stickyTableRegion
                ? { overflowX: "visible", overflowY: "visible" }
                : undefined
            }
          >
            <div
              className={styles.split_header_scroll}
              // The sticky positioning lives here, on the wrapper div, not
              // on <thead>/<th> (tried both - see git history).
              // position:sticky needs its containing block to have real
              // height to scroll through to visually stay pinned; this
              // div's parent (.split_scroll_outer) spans the *entire*
              // table (header + every row), so there's plenty of room for
              // it to stay stuck while the (much taller) body scrolls
              // beneath it. The header-only <table> nested inside is just
              // one row tall on its own, which is why sticky directly on
              // <thead>/<th> never had anywhere to stick within - it'd
              // scroll fully past the offset point almost instantly.
              // This div itself must stay overflow:visible (not a scroll
              // container) or it'd hijack the sticky reference away from
              // the real page - the horizontal-scroll wrapper is nested
              // one level deeper instead (below), so its own overflow
              // doesn't affect this element at all.
              style={
                disableInternalScroll && !stickyTableRegion
                  ? {
                      overflow: "visible",
                      position: "sticky",
                      top: "var(--toca-sticky-top-offset, 136px)",
                      zIndex: 10
                    }
                  : undefined
              }
            >
              <div
                ref={headerScrollRef}
                // Hidden, not auto: this wrapper is never meant to be
                // user-scrolled directly - its scrollLeft is driven by
                // handleBodyHorizontalScroll mirroring the body's real,
                // visible scrollbar below, so header and body columns
                // track each other without a second visible scrollbar.
                style={{ overflow: "hidden" }}
              >
                <table className={styles.full_table}>
                  <thead
                    className={headerClassName}
                    style={
                      disableInternalScroll && !stickyTableRegion
                        ? { position: "static", top: "auto" }
                        : undefined
                    }
                  >
                    {theadRow}
                  </thead>
                </table>
              </div>
            </div>
            {(() => {
              const bodyScrollDiv = (
                <div
                  className={`${styles.split_body_scroll} ${stickyTableRegion ? styles.split_body_scroll_fill : ""} ${hideHorizontalScrollbar ? styles.hide_horizontal_scrollbar : ""}`}
                  style={
                    stickyTableRegion
                      ? undefined
                      : disableInternalScroll
                      ? hideHorizontalScrollbar
                        ? {
                            maxHeight: "none",
                            overflowX: "auto",
                            overflowY: "hidden"
                          }
                        : { maxHeight: "none", overflowX: "auto" }
                      : { maxHeight: height ? `${height}px` : "auto" }
                  }
                  ref={containerRef}
                  onScroll={handleBodyHorizontalScroll}
                >
                  <table className={styles.full_table}>{tbodyContent}</table>
                </div>
              );
              return hideHorizontalScrollbar ? (
                <div style={{ overflow: "hidden" }}>{bodyScrollDiv}</div>
              ) : (
                bodyScrollDiv
              );
            })()}
          </div>
        ) : (
          <div
            className={`${styles.table_container} ${disableInternalScroll ? styles.no_internal_scroll : ""}`}
            style={
              disableInternalScroll
                ? { maxHeight: "none", overflowX: "visible", overflowY: "visible" }
                : { maxHeight: height ? `${height}px` : "auto", overflowX: "auto" }
            }
            ref={containerRef}
          >
            <table className={styles.full_table}>
              <thead className={headerClassName}>{theadRow}</thead>
              {tbodyContent}
            </table>
          </div>
        )}
      </div>

      {/* Infinite Scroll Status Bar */}
      {infiniteScroll && sortedData.length > 0 && !loading && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "16px 0",
          gap: "12px"
        }}>
          {loadingMore ? (
            <>
              <div style={{
                display: "flex",
                gap: "6px",
                alignItems: "center"
              }}>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: "#6366f1",
                      animation: "pulse 1.4s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                      opacity: 0.7
                    }}
                  />
                ))}
              </div>
              <span style={{
                fontSize: "13px",
                color: "#6366f1",
                fontWeight: "500"
              }}>
                Loading more entries...
              </span>
              <style>
                {`
                  @keyframes pulse {
                    0%, 80%, 100% {
                      transform: scale(0.6);
                      opacity: 0.4;
                    }
                    40% {
                      transform: scale(1);
                      opacity: 1;
                    }
                  }
                `}
              </style>
            </>
          ) : (
            <>
              <div style={{
                width: "30px",
                height: "1px",
                background: "linear-gradient(to right, transparent, #e5e7eb)"
              }} />
              <span style={{
                fontSize: "13px",
                color: "#6b7280",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}>
                <span style={{ color: "#111827", fontWeight: "600" }}>
                  {sortedData.length}
                </span>
                <span>of</span>
                <span style={{ color: "#111827", fontWeight: "600" }}>
                  {infiniteTotalItems || sortedData.length}
                </span>
                <span>entries</span>
                {!hasMore && (
                  <span style={{
                    marginLeft: "4px",
                    color: "#10b981",
                    fontSize: "12px"
                  }}>
                    ✓ All loaded
                  </span>
                )}
              </span>
              <div style={{
                width: "30px",
                height: "1px",
                background: "linear-gradient(to left, transparent, #e5e7eb)"
              }} />
            </>
          )}
        </div>
      )}

      {/* Pagination Controls - Hidden when infinite scroll is enabled */}
      {showPagination && paginationVariant !== "expanded" && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "12px 0",
          gap: "15px",
          marginTop: "10px"
        }}>
          {totalItems > 0 && (
            <span style={{ fontSize: "14px", color: "#666" }}>
              {pagination ? (
                `Page ${displayPage} of ${totalPages}`
              ) : (
                `Showing ${((displayPage - 1) * PAGE_SIZE) + 1} - ${Math.min(displayPage * PAGE_SIZE, totalItems)} of ${totalItems}`
              )}
            </span>
          )}

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => handlePageChange(displayPage - 1)}
              disabled={displayPage === 1 || paginationLoading}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                backgroundColor: (displayPage === 1 || paginationLoading) ? "#f5f5f5" : "white",
                color: (displayPage === 1 || paginationLoading) ? "#aaa" : "#333",
                cursor: (displayPage === 1 || paginationLoading) ? "not-allowed" : "pointer"
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} size="xs" />
            </button>

            {pageNumbers.map((p, index) =>
              p === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  style={{ padding: "0 4px", color: "#999" }}
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  disabled={paginationLoading}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    backgroundColor: p === displayPage ? "#6366f1" : (paginationLoading ? "#f5f5f5" : "white"),
                    color: p === displayPage ? "white" : (paginationLoading ? "#aaa" : "#333"),
                    fontWeight: p === displayPage ? 600 : 400,
                    cursor: paginationLoading ? "not-allowed" : "pointer"
                  }}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => handlePageChange(displayPage + 1)}
              disabled={displayPage >= totalPages || paginationLoading}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                backgroundColor: (displayPage >= totalPages || paginationLoading) ? "#f5f5f5" : "white",
                color: (displayPage >= totalPages || paginationLoading) ? "#aaa" : "#333",
                cursor: (displayPage >= totalPages || paginationLoading) ? "not-allowed" : "pointer"
              }}
            >
              <FontAwesomeIcon icon={faChevronRight} size="xs" />
            </button>
          </div>
        </div>
      )}

      {/* Expanded pagination footer (Billing Dashboard only, opt-in via
          paginationVariant="expanded"): Back/Next text buttons, a windowed
          page-number list, a centered item-range summary, and a boxed
          page-size dropdown on the right. */}
      {showExpandedFooter && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          padding: "10px 16px",
          gap: "12px",
          ...(stickyTableRegion
            ? { flexShrink: 0, background: "#fff", borderTop: "1px solid #e7e7e7" }
            : {}),
          // disableInternalScroll: the page (not a bounded inner box) owns
          // vertical scroll here, same as the header's own position:sticky
          // above (see headerClassName/.no_internal_scroll) - sticking the
          // footer to the bottom of that same real scroll context keeps it
          // visible the same way the header stays visible at the top,
          // instead of only showing once the user scrolls past every row.
          ...(disableInternalScroll && !stickyTableRegion
            ? {
                position: "sticky",
                bottom: 0,
                background: "#fff",
                borderTop: "1px solid #e7e7e7",
                zIndex: 2
              }
            : {})
        }}>
          {/* Left column always reserves its grid cell, even empty, so the
              center summary stays anchored regardless of whether the nav
              buttons render (single-page results hide them). */}
          <div>
          {showPagination && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button
                onClick={() => handlePageChange(displayPage - 1)}
                disabled={displayPage === 1 || paginationLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  height: "32px",
                  padding: "0 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  backgroundColor: (displayPage === 1 || paginationLoading) ? "#f5f5f5" : "white",
                  color: (displayPage === 1 || paginationLoading) ? "#aaa" : "#333",
                  fontSize: "14px",
                  cursor: (displayPage === 1 || paginationLoading) ? "not-allowed" : "pointer"
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} size="xs" />
                Back
              </button>

              {pageNumbersExpanded.map((p, index) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-expanded-${index}`}
                    style={{ padding: "0 4px", color: "#999" }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    disabled={paginationLoading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      backgroundColor: p === displayPage ? "#1e2a5a" : (paginationLoading ? "#f5f5f5" : "white"),
                      color: p === displayPage ? "white" : (paginationLoading ? "#aaa" : "#333"),
                      fontWeight: p === displayPage ? 600 : 400,
                      fontSize: "14px",
                      cursor: paginationLoading ? "not-allowed" : "pointer"
                    }}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={() => handlePageChange(displayPage + 1)}
                disabled={displayPage >= totalPages || paginationLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  height: "32px",
                  padding: "0 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  backgroundColor: (displayPage >= totalPages || paginationLoading) ? "#f5f5f5" : "white",
                  color: (displayPage >= totalPages || paginationLoading) ? "#aaa" : "#333",
                  fontSize: "14px",
                  cursor: (displayPage >= totalPages || paginationLoading) ? "not-allowed" : "pointer"
                }}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} size="xs" />
              </button>
            </div>
          )}
          </div>

          <div style={{ textAlign: "center" }}>
            {totalItems > 0 && (
              <span style={{ fontSize: "14px", color: "#333", fontWeight: 500 }}>
                {pageSize
                  ? `${((displayPage - 1) * pageSize) + 1}–${Math.min(displayPage * pageSize, totalItems)} of ${totalItems.toLocaleString()}`
                  : `Page ${displayPage} of ${totalPages}`}
              </span>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
          {pageSize != null && onPageSizeChange && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "14px", color: "#666" }}>Result per page</span>
              <Select
                value={String(pageSize)}
                onChange={value => onPageSizeChange(Number(value))}
              >
                <SelectTrigger
                  style={{
                    width: "72px",
                    height: "32px",
                    border: "1px solid #d0d0d0",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    fontSize: "13px",
                    padding: "4px 8px"
                  }}
                >
                  <SelectValue placeholder="Page size">{pageSize}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {[50, 100, 150, 200].map(size => (
                    <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;